module.exports = new Promise(resolve => {
    const formattedAppName = 'mf-commerce'
    const appName = 'mf_commerce'
    const cacheKey = 'remoteBranch-' + formattedAppName

    // Get branch name with localStorage caching.
    // window.remotes is intentionally NOT used here — it holds full devspace URLs,
    // not branch names. Reading it here would corrupt the localStorage cache with
    // a URL, breaking CDN asset paths in non-devspace environments.
    const getBranchName = () => {
      const cachedBranch = localStorage.getItem(cacheKey)
      if (cachedBranch) {
        // Guard against stale localStorage entries that accidentally stored a full URL
        // instead of a branch name (e.g. from the old mfe_support config UI). Using a URL
        // as the branch segment produces malformed CDN paths like
        // assets-devspaces.calendly.com/mfe/oap-web/https://…/remoteEntry.js/remoteEntry.js.
        if (cachedBranch.startsWith('http://') || cachedBranch.startsWith('https://')) {
          console.warn('Clearing invalid remoteBranch cache for', appName, '(contained a URL, not a branch name):', cachedBranch)
          localStorage.removeItem(cacheKey)
          return 'main'
        }
        console.debug('Using cached branch for', appName, cachedBranch)
        return cachedBranch
      }
      return 'main'
    }

    // Resolve remote URL based on environment.
    //
    // Priority model for devspace engineers:
    //   1. Explicit localStorage override (remoteBranch-<app>) → CDN bucket at that branch.
    //      Set this via mfe_support/config to load a colleague's PR from the bucket instead
    //      of your own running webpack-dev-server.  Clear the field to go back to the server.
    //   2. No localStorage override + window.remotes has a URL for this app → devspace
    //      webpack-dev-server (injected by caldev).  Default when your MFE is running.
    //   3. No localStorage override + no window.remotes → CDN URL resolved by environment.
    const getRemoteURL = () => {
      // Check for an explicit user-set branch override in localStorage.
      // null  = not set  → fall through to window.remotes / environment detection.
      // value = set      → user wants the CDN bucket at this branch, even if window.remotes
      //                    has a live devspace URL for this app.
      const storedBranch = localStorage.getItem(cacheKey)

      if (storedBranch === null) {
        // No explicit override → injected devspace server URL wins when present.
        const injectedRemoteURL = window.remotes?.[appName]
        if (injectedRemoteURL) {
          return injectedRemoteURL
        }
      }

      // Explicit branch override OR no devspace URL available → resolve via CDN/environment.
      // getBranchName() returns the stored value (with URL-corruption guard) or 'main'.
      const branchName = getBranchName()

      // Marina: branch value 'marina' forces local HTTPS regardless of environment URL.
      // Useful when the monolith is accessed via a non-local URL but MFEs are running locally.
      if (branchName === 'marina') {
        return 'https://' + formattedAppName + '.local.calendly-internal.com/remoteEntry.js'
      }

      // Local development (hostname-based detection for standard marina setups)
      if (window.location.hostname.includes('local')) {
        return 'http://' + formattedAppName + '.local.calendly-internal.com/remoteEntry.js'
      }

      // Review environments
      if (window.location.hostname.includes('review')) {
        return 'https://assets-' + window.location.hostname + '/mfe/' + formattedAppName + '/' + branchName + '/remoteEntry.js'
      }

      // DevSpaces environments — CDN bucket path, branch from localStorage or 'main'.
      // window.remotes was already checked above (and returned early) when no override is set.
      if (window.location.hostname.includes('spaces')) {
        return 'https://assets-devspaces.calendly.com/mfe/' + formattedAppName + '/' + branchName + '/remoteEntry.js'
      }

      // Staging environment
      if (window.location.hostname.includes('staging')) {
        // Signadot sandboxes use assets-devspaces as their asset host
        if (window.WEBPACK_PUBLIC_PATH?.includes('//assets-devspaces.calendly.com/')) {
          return 'https://assets-devspaces.calendly.com/mfe/' + formattedAppName + '/' + branchName + '/remoteEntry.js'
        }
        return 'https://assets-staging.calendly.com/mfe/' + formattedAppName + '/remoteEntry.js'
      }

      // Production
      return 'https://assets.calendly.com/mfe/' + formattedAppName + '/remoteEntry.js'
    }

    // Resolve URL once so crossOrigin can be derived from the actual source.
    const remoteURL = getRemoteURL()
    const isDevspace = remoteURL === window.remotes?.[appName]

    // When loading from a devspace dev server, also load its HMR client entry
    // (dev.js) so that webpack-dev-server can open a WebSocket from the host
    // browser context.  Without this, the client is compiled but never executed:
    // webpack-dev-server has no regular entries to inject into (only the MF
    // container), so the client chunk is unreachable from remoteEntry.js alone.
    // Loading dev.js gives the host the live-reload / HMR WebSocket, and because
    // webpack falls back to a full-page reload when no module accepts an HMR
    // update, changes trigger an automatic refresh as soon as the rebuild is done.
    if (isDevspace && !document.querySelector('script[data-mfe-hmr="mf_commerce"]')) {
      try {
        const devURL = new URL(remoteURL)
        devURL.pathname = '/dev.js'
        devURL.search = '?t=' + Date.now()
        const devScript = document.createElement('script')
        devScript.src = devURL.toString()
        devScript.crossOrigin = 'use-credentials'
        devScript.async = true
        devScript.setAttribute('data-mfe-hmr', appName)
        // If dev.js doesn't exist (e.g. the remote hasn't adopted the dev-entry
        // convention yet), swallow the 404 — it doesn't break anything.
        devScript.onerror = () => {}
        document.head.appendChild(devScript)
      } catch {
        // URL constructor can throw for non-http(s) remoteURLs; silently skip.
      }
    }

    // Create and configure script tag
    const script = document.createElement('script')
    // In devspace mode the URL goes through Cloudflare's edge cache, which honours
    // the origin's Cache-Control only loosely.  Append a timestamp so every page
    // load produces a unique URL → always a Cloudflare cache MISS → webpack-dev-
    // server returns the most-recently compiled remoteEntry.js.
    script.src = isDevspace ? remoteURL + '?t=' + Date.now() : remoteURL
    script.async = true
    // Use credentials only when we're actually loading from the injected devspace
    // URL. Deriving from remoteURL (rather than re-checking window.remotes) ensures
    // crossOrigin stays in sync with script.src regardless of environment.
    script.crossOrigin = isDevspace ? 'use-credentials' : 'anonymous'

    // Handle successful script load
    script.onload = () => {
      // Try formatted name first, then original name
      const remoteEntry = window[formattedAppName] || window[appName]
      
      if (!remoteEntry) {
        console.error('Remote entry "' + appName + '" not found on window after script load')
        resolve({
          get: () => Promise.reject(new Error('Remote ' + appName + ' not available')),
          init: () => console.warn('Remote ' + appName + ' not available')
        })
        return
      }

      // Resolve with remoteEntry directly (not a proxy) so that Module Federation's
      // loadEntry hook can properly wrap remoteEntryExports.get() with runtime plugins
      // (retry-plugin, performance-plugin). If we create a proxy here, the proxy's
      // get() method would have a closure over remoteEntry and bypass the wrapped version.
      // By resolving with remoteEntry directly, Module Federation will pass it to
      // loadEntry hooks, plugins will wrap it, and all calls will go through the wrapped version.
      resolve(remoteEntry)
    }

    // Handle script load errors
    script.onerror = () => {
      console.error('Failed to load remote entry for', appName)
      localStorage.removeItem(cacheKey) // Clear stale cache
      resolve({
        get: () => Promise.reject(new Error('Remote ' + appName + ' not available')),
        init: () => console.warn('Remote ' + appName + ' not available')
      })
    }

    // Inject script into document
    document.head.appendChild(script)
  });